import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFloating, offset } from '@floating-ui/react-dom';

import styles from './styles.module.scss';

// used package (floating-ui) document: https://floating-ui.com/docs
function Tooltip({
  children,
  placement,
  message,
  additionalStyle,
  offsetValue,
}) {
  const [showTooltip, setShowTooltip] = useState(false);

  const { x, y, strategy, refs, update } = useFloating({
    placement,
    middleware: [offset(offsetValue)],
  });

  const tooltipStyles = {
    position: strategy,
    top: y ?? 0,
    left: x ?? 0,
    ...additionalStyle,
  };

  const handleMouseOver = () => setShowTooltip(true);
  const handleMouseOut = () => setShowTooltip(false);
  const handleTouchStart = (e) => {
    e.preventDefault();
    setShowTooltip((prev) => !prev);
  };

  useEffect(() => {
    update();
  }, [showTooltip]);

  return (
    <>
      {React.cloneElement(children, {
        onMouseOver: handleMouseOver,
        onMouseOut: handleMouseOut,
        onTouchStart: handleTouchStart,
        ref: refs.setReference,
      })}
      <div
        ref={refs.setFloating}
        style={tooltipStyles}
        className={`${styles.tooltip_content} ${
          showTooltip ? styles.visible : ''
        }`}
      >
        {message}
      </div>
    </>
  );
}

Tooltip.defaultProps = {
  placement: 'bottom',
  offsetValue: 0,
};

Tooltip.propTypes = {
  children: PropTypes.element.isRequired,
  placement: function (props, propName, componentName) {
    // the placement can be either top,bottom,left,right or their combinations with start,end
    // example: top,bottom-end,left-start,...
    if (!/^(top|bottom|right|left)($|-(start|end))$/.test(props[propName])) {
      return new Error(
        'Invalid prop `' +
          propName +
          '` supplied to' +
          ' `' +
          componentName +
          '`. Validation failed.'
      );
    }
  },
  additionalStyle: PropTypes.object,
  message: PropTypes.string.isRequired,

  offsetValue: PropTypes.oneOfType([
    // tooltip movement just in main axis
    PropTypes.number,
    // for top and bottom main axis is vertical and crossAxis is horizontal
    // for left, right its vice versa.
    PropTypes.shape({
      mainAxis: PropTypes.number,
      crossAxis: PropTypes.number,
    }),
  ]),
};

export default Tooltip;
